// Live screen snapshots (Sprint 8). The CMS asks a screen to capture what it's
// showing right now: POST queues the request (202), then the player uploads a
// frame that appears in the snapshots list. The image itself is served from the
// API behind the session cookie, so a plain <img src> renders it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, request } from './client.ts';
import { qk } from './keys.ts';

/** How the frame was produced. 'native' = a real device screen-capture (Android
 *  player); 'dom' = an approximate render of the browser player's DOM. */
export type SnapshotKind = 'native' | 'dom';

export interface Snapshot {
  id: string;
  kind: SnapshotKind;
  taken_at: number;
  width: number;
  height: number;
}

export interface SnapshotRequestResult {
  requested_at: number;
}

export interface SnapshotList {
  snapshots: Snapshot[];
}

export function requestSnapshot(screenId: string): Promise<SnapshotRequestResult> {
  return request<SnapshotRequestResult>(`/screens/${screenId}/snapshot`, { method: 'POST' });
}

export function useRequestSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (screenId: string) => requestSnapshot(screenId),
    onSuccess: (_res, screenId) => qc.invalidateQueries({ queryKey: qk.snapshots(screenId) }),
  });
}

export function listSnapshots(screenId: string): Promise<Snapshot[]> {
  return request<SnapshotList>(`/screens/${screenId}/snapshots`).then((r) => r.snapshots);
}

/**
 * The screen's snapshots, newest first. `pollMs` drives react-query's
 * `refetchInterval` while we're waiting for a freshly requested capture; pass
 * `false` to stop polling once one arrives (or the 90s window lapses).
 */
export function useSnapshots(screenId: string, enabled: boolean, pollMs: number | false) {
  return useQuery({
    queryKey: qk.snapshots(screenId),
    queryFn: () => listSnapshots(screenId),
    enabled,
    refetchInterval: pollMs,
    // Newest first, defensively (the API already sorts, but the UI depends on it).
    select: (rows) => [...rows].sort((a, b) => b.taken_at - a.taken_at),
  });
}

/** Session-authenticated URL for a snapshot image — usable directly as <img src>. */
export function snapshotImageUrl(screenId: string, snapshotId: string): string {
  return `${API_BASE}/screens/${screenId}/snapshots/${snapshotId}/file`;
}
